import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, Popover, Segmented, Typography } from 'antd';
import {
  AppstoreOutlined,
  CommentOutlined,
  DownOutlined,
  FileTextOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Glp1BridgeCompactTapNativeAds from '../../components/Glp1BridgeCompactTapNativeAds';
import Glp1BridgeChatPanel from './Glp1BridgeChat';
import Glp1BridgeProviderAccessOverlay from '../../components/Glp1BridgeProviderAccessOverlay';
import useGlp1BridgeProviderAccess from '../../components/useGlp1BridgeProviderAccess';
import { logoutGlp1BridgeAccess, recordGlp1BridgeScreenerEvent } from '../../api/glp1BridgeChat';
import { recordClickEvent } from '../../utils/trackClick';
import './Glp1BridgeAssistant.css';

const { Title } = Typography;

const GLP1_ASSISTANT_SUBJECT = 'resources:glp1-bridge-assistant';
const GLP1_GUIDED_SCREENER_MESSAGE_SOURCE = 'glp1_bridge_guided_screener';
const GLP1_GUIDED_SCREENER_EVENT_NAMES = new Set([
  'guided_screener_loaded',
  'guided_screener_started',
  'guided_screener_step_viewed',
  'guided_screener_step_completed',
  'guided_screener_result_shown',
  'guided_screener_summary_downloaded',
  'guided_screener_pa_form_downloaded',
  'guided_screener_restarted',
  'guided_screener_back_clicked',
]);

const disclaimerPopoverContent = (
  <div className="glp1-disclaimer-popover">
    <div className="glp1-disclaimer-popover-title">Disclaimer</div>
    <p>This assistant provides workflow guidance from CMS-source materials. It is not a final coverage determination.</p>
    <p>Coverage varies by plan documents, policy updates, medical-necessity review, and individual case. Verify before prescribing or routing.</p>
    <p>Use de-identified facts only. Do not enter names, dates of birth, Medicare IDs, MRNs, addresses, phone, email, ZIP codes, or specific service dates.</p>
  </div>
);

const bridgeResourceMenuItems = [
  {
    key: 'hub',
    icon: <HomeOutlined aria-hidden="true" />,
    label: (
      <Link
        className="glp1-resource-menu-link"
        to="/resources/glp1-bridge"
        aria-label="Resource hub, choose assistant, recording, or guide"
      >
        <span className="glp1-resource-menu-title">Resource hub</span>
        <span className="glp1-resource-menu-copy">Choose assistant, recording, or guide.</span>
      </Link>
    ),
  },
  {
    key: 'recording',
    icon: <PlayCircleOutlined aria-hidden="true" />,
    label: (
      <Link
        className="glp1-resource-menu-link"
        to="/resources/glp1-bridge/recording"
        aria-label="Recording and slides, watch the walkthrough and review the deck"
      >
        <span className="glp1-resource-menu-title">Recording + slides</span>
        <span className="glp1-resource-menu-copy">Watch the walkthrough and review the deck.</span>
      </Link>
    ),
  },
  {
    key: 'guide',
    icon: <FileTextOutlined aria-hidden="true" />,
    label: (
      <Link
        className="glp1-resource-menu-link"
        to="/resources/glp1-bridge/guide"
        aria-label="Clinical guide, read eligibility, billing, and CMS source links"
      >
        <span className="glp1-resource-menu-title">Clinical guide</span>
        <span className="glp1-resource-menu-copy">Read eligibility, billing, and CMS source links.</span>
      </Link>
    ),
  },
];

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function parseGuidedScreenerTelemetryMessage(messageEvent) {
  if (messageEvent.origin !== window.location.origin) return null;
  const { data } = messageEvent;
  if (!isPlainObject(data)) return null;
  if (data.source !== GLP1_GUIDED_SCREENER_MESSAGE_SOURCE) return null;
  if (!GLP1_GUIDED_SCREENER_EVENT_NAMES.has(data.event_name)) return null;
  if (data.payload !== undefined && !isPlainObject(data.payload)) return null;
  return {
    eventName: data.event_name,
    payload: data.payload || {},
  };
}

export default function Glp1BridgeAssistantPage() {
  const [activeMode, setActiveMode] = useState('screener');
  const [resourceMenuOpen, setResourceMenuOpen] = useState(false);
  const [disclaimerPopoverOpen, setDisclaimerPopoverOpen] = useState(false);
  const {
    accessLoading,
    accessUser,
    accessError,
    handleVerified,
    setAccessError,
    setAccessUser,
  } = useGlp1BridgeProviderAccess();

  useEffect(() => {
    recordClickEvent({
      subject: GLP1_ASSISTANT_SUBJECT,
      body: 'Visited the Medicare GLP-1 Bridge assistant page',
    });
  }, []);

  useEffect(() => {
    const openScreener = () => setActiveMode('screener');
    window.addEventListener('glp1-bridge-start-screener', openScreener);
    return () => window.removeEventListener('glp1-bridge-start-screener', openScreener);
  }, []);

  const handleAccessRequired = useCallback(() => {
    setAccessUser(null);
    setActiveMode('screener');
    setAccessError('Please verify your professional email to continue.');
  }, [setAccessError, setAccessUser]);

  const handleLogout = useCallback(async () => {
    try {
      await logoutGlp1BridgeAccess();
    } finally {
      setAccessUser(null);
      setActiveMode('screener');
    }
  }, [setAccessUser]);

  const handleResourceMenuOpenChange = useCallback((open) => {
    setResourceMenuOpen(open);
    if (open) {
      setDisclaimerPopoverOpen(false);
    }
  }, []);

  const handleDisclaimerPopoverOpenChange = useCallback((open) => {
    setDisclaimerPopoverOpen(open);
    if (open) {
      setResourceMenuOpen(false);
    }
  }, []);

  const hasAccess = Boolean(accessUser);
  const showLockedPreview = accessLoading || !hasAccess;

  useEffect(() => {
    if (!hasAccess) return undefined;

    const handleGuidedScreenerTelemetry = (messageEvent) => {
      const telemetry = parseGuidedScreenerTelemetryMessage(messageEvent);
      if (!telemetry) return;
      recordGlp1BridgeScreenerEvent(telemetry).catch(() => {});
    };

    window.addEventListener('message', handleGuidedScreenerTelemetry);
    return () => window.removeEventListener('message', handleGuidedScreenerTelemetry);
  }, [hasAccess]);

  return (
    <div className={`glp1-assistant-page${hasAccess ? ' glp1-assistant-page--with-rail' : ''}`}>
      {hasAccess ? (
        <aside className="glp1-assistant-rail" aria-label="GLP-1 Bridge assistant navigation">
          <button
            type="button"
            className={`glp1-rail-action${activeMode === 'screener' ? ' is-active' : ''}`}
            title="Eligibility Screener"
            aria-label="Eligibility Screener"
            aria-current={activeMode === 'screener' ? 'page' : undefined}
            onClick={() => setActiveMode('screener')}
          >
            <SafetyCertificateOutlined aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`glp1-rail-action${activeMode === 'ask' ? ' is-active' : ''}`}
            title="Ask Bridge Question"
            aria-label="Ask Bridge Question"
            aria-current={activeMode === 'ask' ? 'page' : undefined}
            onClick={() => setActiveMode('ask')}
          >
            <CommentOutlined aria-hidden="true" />
          </button>
          <span className="glp1-rail-spacer" />
        </aside>
      ) : null}

      <div className="glp1-assistant-shell">
        <header className="glp1-assistant-header">
          <div className="glp1-assistant-header-inner">
            <div className="glp1-assistant-topbar">
              <div className="glp1-assistant-title-group">
                <Title level={1} className="glp1-assistant-heading">
                  Medicare GLP-1 Bridge Assistant
                </Title>
              </div>

              <div className="glp1-assistant-actions">
                <div className="glp1-assistant-utility-actions">
                  <Dropdown
                    menu={{ items: bridgeResourceMenuItems }}
                    open={resourceMenuOpen}
                    onOpenChange={handleResourceMenuOpenChange}
                    overlayClassName="glp1-assistant-resource-menu"
                    placement="bottomRight"
                    trigger={['hover', 'click']}
                    mouseEnterDelay={0.08}
                    mouseLeaveDelay={0.16}
                  >
                    <Button
                      type="text"
                      className="glp1-assistant-resource-trigger"
                      icon={<AppstoreOutlined aria-hidden="true" />}
                    >
                      GLP-1 Bridge resources
                      <DownOutlined className="glp1-assistant-resource-caret" aria-hidden="true" />
                    </Button>
                  </Dropdown>
                  <Popover
                    content={disclaimerPopoverContent}
                    open={disclaimerPopoverOpen}
                    onOpenChange={handleDisclaimerPopoverOpenChange}
                    overlayClassName="glp1-assistant-disclaimer-popover"
                    placement="bottomRight"
                    trigger={['hover', 'focus', 'click']}
                    mouseEnterDelay={0.08}
                    mouseLeaveDelay={0.16}
                  >
                    <Button type="text" icon={<InfoCircleOutlined aria-hidden="true" />}>
                      Disclaimer
                    </Button>
                  </Popover>
                  {hasAccess ? (
                    <Button type="text" icon={<LogoutOutlined aria-hidden="true" />} onClick={handleLogout}>
                      Sign out
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            {hasAccess ? (
              <Segmented
                className="glp1-mode-switch"
                value={activeMode}
                onChange={setActiveMode}
                options={[
                  {
                    label: (
                      <span>
                        <SafetyCertificateOutlined aria-hidden="true" />
                        Eligibility Screener
                      </span>
                    ),
                    value: 'screener',
                  },
                  {
                    label: (
                      <span>
                        <CommentOutlined aria-hidden="true" />
                        Ask Bridge Question
                      </span>
                    ),
                    value: 'ask',
                  },
                ]}
              />
            ) : null}
          </div>
        </header>

        {hasAccess ? (
          <Glp1BridgeChatPanel
            activeMode={activeMode}
            onModeChange={setActiveMode}
            onAccessRequired={handleAccessRequired}
          />
        ) : showLockedPreview ? (
          <Glp1BridgeProviderAccessOverlay
            accessError={accessError}
            accessLoading={accessLoading}
            checkingCopy="If this browser already has a valid provider cookie, the screener will unlock automatically."
            onVerified={handleVerified}
            previewClassName="glp1-locked-assistant-preview"
            previewTestId="glp1-locked-assistant-preview"
          >
            <Glp1BridgeChatPanel
              activeMode="screener"
              onModeChange={() => {}}
              onAccessRequired={handleAccessRequired}
            />
          </Glp1BridgeProviderAccessOverlay>
        ) : null}

        <div className="glp1-assistant-bottom-ad">
          <Glp1BridgeCompactTapNativeAds placement="glp1_bridge_assistant_bottom" />
        </div>
      </div>
    </div>
  );
}
